import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Flame, Loader2, Check, X, ChevronRight, ChevronLeft } from "lucide-react";

type UsernameStatus = "idle" | "checking" | "available" | "taken" | "invalid";

export default function OnboardingPage() {
  const [, setLocation] = useLocation();
  const { user, isLoading } = useAuth();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>("idle");
  const [usernameMessage, setUsernameMessage] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [dobError, setDobError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isPending, setIsPending] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation("/auth");
    }
    if (user && user.onboardingComplete) {
      setLocation("/");
    }
  }, [user, isLoading, setLocation]);

  useEffect(() => {
    if (user?.username && !initialized.current) {
      initialized.current = true;
      setUsername(user.username);
      checkUsername(user.username);
    }
  }, [user]);

  const checkUsername = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const stripped = value.replace(/^@/, "").trim();
    if (!stripped) {
      setUsernameStatus("idle");
      setUsernameMessage("");
      return;
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(stripped)) {
      setUsernameStatus("invalid");
      setUsernameMessage("3-20 characters, letters, numbers and underscores only");
      return;
    }

    setUsernameStatus("checking");
    setUsernameMessage("Checking...");

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/auth/check-username?username=${encodeURIComponent(stripped)}`);
        const data = await res.json();
        if (data.available) {
          setUsernameStatus("available");
          setUsernameMessage("Username available");
        } else if (data.reason === "taken") {
          setUsernameStatus("taken");
          setUsernameMessage("Username taken, try another");
        } else {
          setUsernameStatus("invalid");
          setUsernameMessage("3-20 characters, letters, numbers and underscores only");
        }
      } catch {
        setUsernameStatus("idle");
        setUsernameMessage("Could not check availability");
      }
    }, 400);
  }, []);

  function handleUsernameChange(value: string) {
    const stripped = value.replace(/^@/, "");
    setUsername(stripped);
    checkUsername(stripped);
  }

  function validateAge(dob: string): boolean {
    if (!dob) return false;
    const birth = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear() -
      (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
    return age >= 18;
  }

  function handleDobBlur() {
    if (dateOfBirth && !validateAge(dateOfBirth)) {
      setDobError("You must be 18 or older");
    } else {
      setDobError("");
    }
  }

  const step1Valid = usernameStatus === "available";
  const step2Valid = dateOfBirth.length > 0 && validateAge(dateOfBirth) && gender.length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");
    setIsPending(true);
    try {
      await apiRequest("POST", "/api/auth/onboarding", {
        username: username.trim(),
        dateOfBirth,
        gender,
        accountType: "user",
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Welcome to Buzzn!", description: "You're all set." });
      setLocation("/");
    } catch (err: any) {
      const msg = err.message || "Something went wrong";
      setSubmitError(msg.includes(":") ? msg.split(": ").slice(1).join(": ") : msg);
    } finally {
      setIsPending(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  const inputCls = "bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-11 rounded-xl";
  const labelCls = "text-zinc-400 text-sm mb-1 block";

  const statusIcon = usernameStatus === "checking" ? (
    <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
  ) : usernameStatus === "available" ? (
    <Check className="w-4 h-4 text-green-500" />
  ) : usernameStatus === "taken" || usernameStatus === "invalid" ? (
    <X className="w-4 h-4 text-red-500" />
  ) : null;

  const statusColor = usernameStatus === "available" ? "text-green-500"
    : usernameStatus === "taken" || usernameStatus === "invalid" ? "text-red-400"
    : "text-zinc-500";

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-6 pb-8 pt-[max(2rem,calc(env(safe-area-inset-top)+1rem))]">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Flame className="w-7 h-7 text-red-500" />
            <h1 className="text-3xl font-display font-bold text-white tracking-tight">Buzzn</h1>
          </div>
          <p className="text-zinc-500 text-sm">Complete your profile to get started</p>
        </div>

        {user?.avatarUrl && (
          <div className="flex justify-center mb-6">
            <img
              src={user.avatarUrl}
              alt="Profile"
              className="w-16 h-16 rounded-full border-2 border-primary"
              referrerPolicy="no-referrer"
            />
          </div>
        )}

        <div className="text-center mb-6">
          <p className="text-white text-sm">
            Welcome, <span className="text-primary font-semibold">{user?.displayName || user?.username}</span>
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all ${
                s <= step ? "bg-primary w-10" : "bg-zinc-800 w-6"
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className={labelCls}>Choose your username</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">@</span>
                <Input
                  data-testid="input-onboarding-username"
                  type="text"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="username"
                  className={`${inputCls} pl-7 pr-9`}
                  maxLength={20}
                  autoFocus
                />
                {statusIcon && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">{statusIcon}</span>
                )}
              </div>
              {usernameMessage && (
                <p className={`text-xs mt-1.5 ${statusColor}`} data-testid="text-username-status">
                  {usernameMessage}
                </p>
              )}
            </div>

            <Button
              data-testid="button-onboarding-next"
              type="button"
              disabled={!step1Valid}
              onClick={() => setStep(2)}
              className="w-full h-11 rounded-xl bg-primary hover:bg-primary text-primary-foreground font-bold disabled:opacity-40"
            >
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className={labelCls}>Date of Birth</Label>
              <Input
                data-testid="input-onboarding-dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => { setDateOfBirth(e.target.value); setDobError(""); }}
                onBlur={handleDobBlur}
                className={inputCls}
                required
              />
              {dobError && <p className="text-red-400 text-xs mt-1">{dobError}</p>}
            </div>

            <div>
              <Label className={labelCls}>Gender</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger data-testid="select-onboarding-gender" className={inputCls}>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non-binary">Non-binary</SelectItem>
                  <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {submitError && <p className="text-red-400 text-sm">{submitError}</p>}

            <div className="flex gap-3">
              <Button
                data-testid="button-onboarding-back"
                type="button"
                onClick={() => setStep(1)}
                variant="outline"
                className="h-11 rounded-xl border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                data-testid="button-complete-onboarding"
                type="submit"
                disabled={isPending || !step2Valid}
                className="flex-1 h-11 rounded-xl bg-primary hover:bg-primary text-primary-foreground font-bold disabled:opacity-40"
              >
                {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get started"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
